import ajaxService from "AjaxService";
import { getCachedPromise } from "CachedPromise";
import global from "Global";
import { ResourceStringsNotLoadedError } from "ResourceStringsNotLoadedError";
import * as stringUtils from "StringUtils";

export interface ResourceString extends ResStringData {
  caption: string;
  longCaption?: string;
}

export interface ResStringData {
  short?: string;
  medium?: string;
  long?: string;
  description?: string;
}

/*! StartNoStringValidationRegion These properties define routes */
const captionDataUri = global.serviceUri + "captionservice/captiondata/HTML";
const entityMappingUri = global.serviceUri + "captionservice/entitytokeymappings";
const moduleCaptionUri = global.serviceUri + stringUtils.format("captionservice/moduleCaptions/{0}", global.moduleName);
/*! EndNoStringValidationRegion */

export class ResStringRepository {
  private stringsLoaded = false;
  private stringsCache: Record<string, ResourceString> = {};
  private entityToKeyMappingsCache: Record<string, string[]> = {};
  private loadCaptionLanguageCode = "";
  private loadCaptionDataAsync?: () => Promise<Record<string, ResStringData>>;
  private loadEntityMappingsAsync?: () => Promise<Record<string, string[]>>;

  async loadStringsAsync(languageCode: string): Promise<void> {
    if (!this.loadCaptionDataAsync || this.loadCaptionLanguageCode !== languageCode) {
      this.loadCaptionLanguageCode = languageCode;
      this.loadCaptionDataAsync = getCachedPromise(() =>
        ajaxService.getAsync<Record<string, ResStringData>>(captionDataUri, { languageCode }),
      );
    }

    if (!this.loadEntityMappingsAsync) {
      this.loadEntityMappingsAsync = getCachedPromise(() =>
        ajaxService.getAsync<Record<string, string[]>>(entityMappingUri),
      );
    }

    try {
      if (!this.loadCaptionDataAsync || !this.loadEntityMappingsAsync) {
        return;
      }

      const [resourceStrings, entityToKeyMappings, moduleResStrings] = await Promise.all([
        this.loadCaptionDataAsync(),
        this.loadEntityMappingsAsync(),
        ajaxService.getAsync<Record<string, ResStringData>>(moduleCaptionUri),
      ]);

      if (this.loadCaptionLanguageCode === languageCode) {
        const mergedResourceStrings = { ...resourceStrings, ...moduleResStrings } as Record<string, ResourceString>;
        Object.values(mergedResourceStrings).forEach((receivedCaption) => {
          receivedCaption.caption = receivedCaption.medium || receivedCaption.long || receivedCaption.short || "";
          receivedCaption.longCaption = receivedCaption.long || receivedCaption.medium || receivedCaption.short;
          receivedCaption.description = receivedCaption.description || receivedCaption.long || receivedCaption.caption;
        });

        this.stringsCache = mergedResourceStrings;
        this.entityToKeyMappingsCache = entityToKeyMappings;
      }
    } finally {
      this.stringsLoaded = true;
    }
  }

  clearCache(): void {
    this.loadCaptionLanguageCode = "";
    this.loadCaptionDataAsync = undefined;
    this.loadEntityMappingsAsync = undefined;
    this.entityToKeyMappingsCache = {};
    this.stringsCache = {};
    this.stringsLoaded = false;
  }

  getResourceStrings(key: string): ResourceString | undefined {
    if (!this.stringsLoaded) {
      /*! SuppressStringValidation message of error */
      throw new ResourceStringsNotLoadedError("Cannot load resource strings before translations have been loaded.");
    }

    if (key) {
      return this.stringsCache[key.toUpperCase()];
    }

    throw new Error("Empty key was provided to getResourceStrings");
  }

  getKeysForEntity(entityName: string): string[] {
    return this.entityToKeyMappingsCache[entityName] || [];
  }
}

export default new ResStringRepository();
